import { useEffect, useRef, useState } from "react";
import { contact } from "@/data/contact";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const FAQ_DEFAULT = [
  { q: "What does Gold Petroleum do?", a: "We are a UAE-registered trading and distribution company with two divisions: Petroleum & Energy, and non-manufactured Precious Metals." },
  { q: "Which products in Petroleum & Energy?", a: "Crude oil, diesel & gasoil, jet fuel, gasoline, bitumen, and urea (a related chemical commodity, not a fuel)." },
  { q: "Which precious metals?", a: "Gold doré, silver and platinum — raw, non-manufactured. We do not sell jewellery." },
  { q: "Which regions?", a: "Middle East, Africa and Asia, anchored around the UAE." },
  { q: "Do you own the refineries or tankers?", a: "No. Shipping, storage and refineries listed are partner relationships. Vessels, terminals and refineries are not owned by Gold Petroleum FZ LLC." },
  { q: "How do I get in touch?", a: "Sales enquiries: sales@gpfzllc.com. General enquiries: admin@gpfzllc.com. Company telephone: +972 56 585 0606. Sales direct (Fabio, Canada): +1 416 277 5262." },
  { q: "Which email should I use?", a: "Sales, corporate relationships and partnership questions go to sales@gpfzllc.com. Media, careers and general questions go to admin@gpfzllc.com." },
  { q: "Are there open roles?", a: "No listed positions. If you'd like to be considered, submit an enquiry with the Careers category on the Contact page — it routes to admin@gpfzllc.com." },
];

function mergeFaqFromContent(content) {
  if (!content) return FAQ_DEFAULT;
  return FAQ_DEFAULT.map((row, i) => ({
    q: content[`assistant.faq.${i}.question`] || row.q,
    a: content[`assistant.faq.${i}.answer`] || row.a,
  }));
}

/**
 * Floating website assistant.
 * - Bubble bottom-right on public pages only.
 * - Modes: FAQ (curated), Enquiry (posts to /api/enquiries with source),
 *   Ask (free-text via EMERGENT_LLM_KEY, only when server reports enabled).
 */
export default function Assistant() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("faq"); // faq | ask | enquiry
  const [askState, setAskState] = useState({ ask_enabled: false, reason: "loading" });
  const [askInput, setAskInput] = useState("");
  const [askThread, setAskThread] = useState([]); // {role: 'user'|'bot'|'err', text}
  const [askBusy, setAskBusy] = useState(false);
  const [enq, setEnq] = useState({ name: "", email: "", message: "", consent: false, website: "" });
  const [enqStatus, setEnqStatus] = useState(null);
  const [enqBusy, setEnqBusy] = useState(false);
  const [faq, setFaq] = useState(FAQ_DEFAULT);
  const panelRef = useRef(null);
  const lastFocusFetchRef = useRef(0);
  const versionRef = useRef(0);

  const refetchContent = async () => {
    try {
      const res = await fetch(`${BASE}/api/public/page-content`, { cache: "no-store" });
      if (!res.ok) return;
      const payload = await res.json();
      const content = payload && typeof payload.content === "object" ? payload.content : payload;
      if (payload?.version != null) versionRef.current = payload.version;
      setFaq(mergeFaqFromContent(content));
    } catch { /* ignore */ }
  };

  // Initial fetch on mount
  useEffect(() => { refetchContent(); }, []);

  useEffect(() => {
    fetch(`${BASE}/api/assistant/state`).then((r) => r.json()).then((s) => setAskState(s)).catch(() => setAskState({ ask_enabled: false, reason: "err" }));
  }, []);

  // Refetch on window focus (throttled 10s)
  useEffect(() => {
    const onFocus = () => {
      const now = Date.now();
      if (now - lastFocusFetchRef.current < 10_000) return;
      lastFocusFetchRef.current = now;
      refetchContent();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // On panel open: refetch + poll every 60s while open and page visible
  useEffect(() => {
    if (!open) return;
    refetchContent();
    let timer = null;
    const tick = () => {
      if (document.visibilityState === "visible") refetchContent();
    };
    timer = setInterval(tick, 60_000);
    const onVis = () => {
      if (document.visibilityState === "visible") refetchContent();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submitAsk = async (e) => {
    e.preventDefault();
    const q = askInput.trim();
    if (!q || askBusy) return;
    if (q.length > 500) return;
    setAskBusy(true);
    setAskThread((t) => [...t, { role: "user", text: q }]);
    setAskInput("");
    try {
      const res = await fetch(`${BASE}/api/assistant/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.answer) {
        setAskThread((t) => [...t, { role: "bot", text: data.answer }]);
      } else if (res.status === 429) {
        setAskThread((t) => [...t, { role: "err", text: "You've reached the rate limit for questions. Please try again shortly or email sales@gpfzllc.com." }]);
      } else if (res.status === 503) {
        setAskThread((t) => [...t, { role: "err", text: "The Ask feature is unavailable right now. Please use the FAQ or send an enquiry." }]);
        setAskState({ ask_enabled: false, reason: data.reason || "unavailable" });
      } else {
        setAskThread((t) => [...t, { role: "err", text: "Something went wrong. Please try the FAQ or send an enquiry." }]);
      }
    } catch {
      setAskThread((t) => [...t, { role: "err", text: "Network problem. Please try again shortly." }]);
    } finally {
      setAskBusy(false);
    }
  };

  const submitEnq = async (e) => {
    e.preventDefault();
    if (enqBusy) return;
    setEnqBusy(true);
    setEnqStatus(null);
    try {
      const res = await fetch(`${BASE}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: enq.name,
          email: enq.email,
          category: "General enquiry",
          message: enq.message,
          consent: enq.consent,
          website: enq.website,
          source: "website_assistant",
        }),
      });
      if (res.ok) {
        setEnqStatus("ok");
        setEnq({ name: "", email: "", message: "", consent: false, website: "" });
      } else {
        setEnqStatus("err");
      }
    } catch {
      setEnqStatus("err");
    } finally {
      setEnqBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`gp-assistant__bubble ${open ? "is-open" : ""}`}
        aria-label="Ask Gold Petroleum"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="assistant-bubble"
      >
        {open ? (
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        ) : (
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v10H8l-4 4V5z"/></svg>
        )}
      </button>

      {open && (
        <div
          className="gp-assistant__panel"
          role="dialog"
          aria-label="Gold Petroleum Assistant"
          ref={panelRef}
          data-testid="assistant-panel"
        >
          <header className="gp-assistant__head">
            <div className="gp-assistant__title">
              <span className="gp-eyebrow-num" style={{ color: "var(--gp-gold)" }}>Assistant</span>
              <strong>Gold Petroleum Assistant</strong>
            </div>
            <button
              type="button"
              className="gp-assistant__x"
              aria-label="Close"
              onClick={() => setOpen(false)}
              data-testid="assistant-close"
            >×</button>
          </header>

          <p className="gp-assistant__caption" data-testid="assistant-caption">
            Automated website assistant. Not a live representative.
          </p>

          <div className="gp-assistant__direct" data-testid="assistant-direct">
            {(() => {
              const cp = contact.company_phone;
              return (
                <>
                  <a href={`tel:${cp.tel}`} className="gp-assistant__direct-btn" data-testid="assistant-direct-call">
                    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z"/></svg>
                    Call our team
                  </a>
                  <a href="mailto:sales@gpfzllc.com" className="gp-assistant__direct-btn" data-testid="assistant-direct-email-sales">
                    Sales
                  </a>
                  <a href="mailto:admin@gpfzllc.com" className="gp-assistant__direct-btn" data-testid="assistant-direct-email-general">
                    General
                  </a>
                  <button type="button" className="gp-assistant__direct-btn gp-assistant__direct-btn--primary" onClick={() => setMode("enquiry")} data-testid="assistant-direct-message">
                    Send a message
                  </button>
                </>
              );
            })()}
          </div>

          <div className="gp-assistant__tabs" role="tablist">
            <button role="tab" aria-selected={mode === "faq"} className={`gp-assistant__tab ${mode === "faq" ? "is-active" : ""}`} onClick={() => setMode("faq")} data-testid="assistant-tab-faq">FAQ</button>
            <button role="tab" aria-selected={mode === "ask"} className={`gp-assistant__tab ${mode === "ask" ? "is-active" : ""}`} onClick={() => setMode("ask")} data-testid="assistant-tab-ask">Ask</button>
            <button role="tab" aria-selected={mode === "enquiry"} className={`gp-assistant__tab ${mode === "enquiry" ? "is-active" : ""}`} onClick={() => setMode("enquiry")} data-testid="assistant-tab-enquiry">Contact the team</button>
          </div>

          <div className="gp-assistant__body">
            {mode === "faq" && (
              <ul className="gp-assistant__faq" data-testid="assistant-faq">
                {faq.map((f, i) => (
                  <li key={i}>
                    <details>
                      <summary data-testid={`faq-q-${i}`}>{f.q}</summary>
                      <p data-testid={`faq-a-${i}`}>{f.a}</p>
                    </details>
                  </li>
                ))}
              </ul>
            )}

            {mode === "ask" && (
              <div className="gp-assistant__ask" data-testid="assistant-ask">
                {!askState.ask_enabled ? (
                  <p className="gp-assistant__note">
                    Free-text answering is not available right now. Try the FAQ or send us an enquiry —
                    the team responds directly by email.
                  </p>
                ) : (
                  <>
                    <p className="gp-assistant__note">
                      Ask about the company, divisions, regions, leadership or contact info. Prices,
                      volumes and commercial specifics are handled by the team via email.
                    </p>
                    <div className="gp-assistant__thread" aria-live="polite">
                      {askThread.map((m, i) => (
                        <div key={i} className={`gp-assistant__msg gp-assistant__msg--${m.role}`}>{m.text}</div>
                      ))}
                    </div>
                    <form onSubmit={submitAsk} className="gp-assistant__form">
                      <label className="gp-sr">Ask a question</label>
                      <input
                        type="text"
                        maxLength={500}
                        value={askInput}
                        onChange={(e) => setAskInput(e.target.value)}
                        placeholder="Ask about Gold Petroleum…"
                        aria-label="Ask a question"
                        data-testid="assistant-ask-input"
                      />
                      <button type="submit" disabled={askBusy || !askInput.trim()} data-testid="assistant-ask-submit">
                        {askBusy ? "…" : "Ask"}
                      </button>
                    </form>
                    <p className="gp-assistant__disclaimer" data-testid="assistant-ask-scope">
                      AI answers are limited to public Gold Petroleum information.
                    </p>
                  </>
                )}
              </div>
            )}

            {mode === "enquiry" && (
              <form className="gp-assistant__enquiry" onSubmit={submitEnq} data-testid="assistant-enquiry-form">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={enq.website} onChange={(e) => setEnq({ ...enq, website: e.target.value })} style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }} />
                <label>Name<input required minLength={2} maxLength={120} value={enq.name} onChange={(e) => setEnq({ ...enq, name: e.target.value })} data-testid="assistant-enq-name" /></label>
                <label>Email<input required type="email" maxLength={200} value={enq.email} onChange={(e) => setEnq({ ...enq, email: e.target.value })} data-testid="assistant-enq-email" /></label>
                <label>Message<textarea required minLength={10} maxLength={4000} rows={3} value={enq.message} onChange={(e) => setEnq({ ...enq, message: e.target.value })} data-testid="assistant-enq-message" /></label>
                <label className="gp-assistant__consent">
                  <input type="checkbox" checked={enq.consent} onChange={(e) => setEnq({ ...enq, consent: e.target.checked })} data-testid="assistant-enq-consent" />
                  <span>I consent to Gold Petroleum FZ LLC using this information to respond to my enquiry.</span>
                </label>
                <button type="submit" disabled={enqBusy || !enq.consent} data-testid="assistant-enq-submit">
                  {enqBusy ? "Sending…" : "Send enquiry"}
                </button>
                {enqStatus === "ok" && <p className="gp-assistant__ok" data-testid="assistant-enq-ok">Your message has been received. A member of the Gold Petroleum team will be in touch.</p>}
                {enqStatus === "err" && <p className="gp-assistant__err" data-testid="assistant-enq-err">Something went wrong. Please email sales@gpfzllc.com.</p>}
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
